import { groupArrayByKeys } from '@campus/utils';
import { Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { EduContent, PersonInterface, SectionContentInterface, SectionInterface } from '../../+models';
import { UserQueries } from '../user';
import { getAllEntities as getEduContentDict } from './../edu-content/edu-content.selectors';
import { getBySectionId as getSectionContentsBySectionId } from './../section-content/section-content.selectors';
import { NAME, selectAll, selectEntities, selectIds, selectTotal, State } from './section.reducer';

export const selectSectionState = createFeatureSelector<State>(NAME);

export const getError = createSelector(selectSectionState, (state: State) => state.error);

export const getLoaded = createSelector(selectSectionState, (state: State) => state.loaded);

export const getAll = createSelector(selectSectionState, selectAll);

export const getCount = createSelector(selectSectionState, selectTotal);

export const getIds = createSelector(selectSectionState, selectIds);

export const getAllEntities = createSelector(selectSectionState, selectEntities);

export const getAllForTeacher = createSelector(getAll, (sections: SectionInterface[]): SectionInterface[] => {
  return sections.filter((section) => section.showTeacher);
});

export const getAllForStudent = createSelector(getAll, (sections: SectionInterface[]): SectionInterface[] => {
  return sections.filter((section) => section.showStudent);
});

/**
 * Returns sections filtered based on the current user's role.
 * If user has student role, returns sections visible to students.
 * If user has teacher role (but not student), returns sections visible to teachers.
 * Otherwise returns all sections.
 */
export const getAllForCurrentRole = createSelector(
  getAllForStudent,
  getAllForTeacher,
  getAll,
  UserQueries.getCurrentUser,
  (
    studentSections: SectionInterface[],
    teacherSections: SectionInterface[],
    allSections: SectionInterface[],
    user: PersonInterface
  ): SectionInterface[] => {
    const roles: string[] = user.roles.map((_role) => _role.name);

    if (roles.includes('student')) {
      return studentSections;
    } else if (roles.includes('teacher')) {
      return teacherSections;
    }

    return allSections;
  }
);

/**
 * returns array of objects in the order of the given ids
 * @example
 * section$: SectionInterface[] = this.store.pipe(
    select(SectionQueries.getByIds, { ids: [2, 1, 3] })
  );
 */
export const getByIds = createSelector(selectSectionState, (state: State, props: { ids: number[] }) => {
  return props.ids.map((id) => state.entities[id]);
});

/**
 * returns array of objects in the order of the given ids
 * @example
 * section$: SectionInterface = this.store.pipe(
    select(SectionQueries.getById, { id: 3 })
  );
 */
export const getById = createSelector(
  selectSectionState,
  (state: State, props: { id: number }) => state.entities[props.id]
);

/**
 * Factory function to create a selector that gets sections by eduContentTocId
 * based on a source selector for sections
 */
const createGetByEduContentTocIdSelector = (getSectionsSelector) =>
  createSelector(
    getSectionsSelector,
    getSectionContentsBySectionId,
    getEduContentDict,
    (
      sections: SectionInterface[],
      sectionContentsBySectionId: Dictionary<SectionContentInterface[]>,
      eduContentDict: Dictionary<EduContent>
    ): Dictionary<SectionInterface[]> => {
      const sectionsByEduContentTocId = groupArrayByKeys(sections, ['eduContentTOCId'], undefined, false, (section) => {
        const sectionContents = sectionContentsBySectionId[section.id] || [];

        const filteredSectionContents = sectionContents.filter((sectionContent) => {
          return eduContentDict[sectionContent.eduContentId]?.publishedEduContentMetadata?.showInDigitalBookTab;
        });

        return {
          ...section,
          sectionContents: filteredSectionContents,
        };
      });

      return sectionsByEduContentTocId;
    }
  );

export const getByEduContentTocIdForCurrentRole = createGetByEduContentTocIdSelector(getAllForCurrentRole);
export const getByEduContentTocIdForTeacher = createGetByEduContentTocIdSelector(getAllForTeacher);
export const getByEduContentTocIdForStudent = createGetByEduContentTocIdSelector(getAllForStudent);

/**
 * returns a dictionary of Sections grouped by eduContentTocId
 * @example
 * section$: Dictionary<SectionInterface[]> = this.store.pipe(
 *  select(SectionQueries.getSectionsByEduContentTocId)
 * );
 **/
export const getSectionsByEduContentTocId = createSelector(
  getAll,
  (sections: SectionInterface[]): Dictionary<SectionInterface[]> => {
    return groupArrayByKeys(sections, ['eduContentTOCId']);
  }
);

export const isBookLoaded = (props: { bookId: number }) =>
  createSelector(selectSectionState, (state: State) => !!state.loadedForBook[props.bookId]);

export const isTocLoaded = (props: { tocId: number }) =>
  createSelector(selectSectionState, (state: State) => !!state.loadedForToc[props.tocId]);
